/**
 * Menu Screen
 * 
 * Main menu/settings screen matching lore-native implementation.
 * Shows user profile, account settings, support, and legal sections.
 * Routes to sub-pages: account, verification, help, about.
 * 
 * @module components/menu/MenuScreen
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';
import { getAvatarUrl } from '@/utils/avatarUrl';
import { Button, Spinner } from '@sequelae/lore-core';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { AccountInformationScreen } from './AccountInformationScreen';
import { VerificationScreen } from './VerificationScreen';
import { HelpScreen } from './HelpScreen';
import { AboutScreen } from './AboutScreen';
import { PrivacyScreen } from './PrivacyScreen';
import { NotificationsScreen } from './NotificationsScreen';
import { UserApiService } from '@/services/api/UserApiService';
import { MfeLoader } from '@/services/mfe/MfeLoader';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import './MenuScreen.css';

interface MenuItemProps {
  title: string;
  onPress: () => void;
}

const MenuItem: React.FC<MenuItemProps> = ({ title, onPress }) => (
  <button className="menu-item" onClick={onPress}>
    <span className="menu-item-title">{title}</span>
    <span className="menu-item-arrow">›</span>
  </button>
);

/**
 * Unified Menu Header Component
 * Shows title with optional back button (for sub-pages)
 */
interface MenuHeaderProps {
  title: string;
  onBack?: () => void;
}

const MenuHeader: React.FC<MenuHeaderProps> = ({ title, onBack }) => {
  const handleBackClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Blur the button to remove focus state
    e.currentTarget.blur();
    onBack?.();
  };

  return (
    <div className="menu-header">
      <div className="menu-header-content">
        {onBack && (
          <button className="menu-back-button" onClick={handleBackClick}>
            <ChevronLeftIcon />
          </button>
        )}
        <h2 className="menu-header-title">{title}</h2>
      </div>
    </div>
  );
};

type MenuSubPage = 'account' | 'privacy' | 'notifications' | 'verification' | 'help' | 'help-faqs' | 'help-feedback' | 'about' | 'developer-tools';

export const MenuScreen: React.FC = () => {
  const { user, logout } = useAuth();
  const { profile, isLoading: loading } = useProfile();
  const navigate = useNavigate();
  const { subpage } = useParams<{ subpage?: string }>();
  const [avatarError, setAvatarError] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [verificationData, setVerificationData] = useState<{
    is_verified: boolean;
    is_enabled: boolean;
  } | null>(null);

  // Invitations removed from web client – redirect old links to account
  useEffect(() => {
    if (subpage === 'invitations') {
      navigate('/account', { replace: true });
    }
  }, [subpage, navigate]);

  // Fetch verification status
  useEffect(() => {
    const fetchVerificationStatus = async () => {
      if (!user?.id) {
        return;
      }

      try {
        const data = await UserApiService.getVerificationStatus();
        setVerificationData(data);
      } catch (error) {
        console.error('[MenuScreen] Error fetching verification status:', error);
        setVerificationData(null);
      }
    };

    fetchVerificationStatus();
  }, [user?.id]);

  const handleLogoutClick = () => setShowSignOutModal(true);

  const handleLogoutConfirm = async () => {
    setShowSignOutModal(false);
    try {
      await logout();
      window.location.href = '/auth';
    } catch (error) {
      console.error('[MenuScreen] Sign out error:', error);
      alert('Failed to sign out. Please try again.');
    }
  };

  const handleLogoutCancel = () => setShowSignOutModal(false);

  const handleMenuItem = (route: string) => {
    // Privacy and Notifications use clean top-level routes
    if (route === 'privacy' || route === 'notifications') {
      navigate(`/${route}`);
    } else {
      navigate(`/account/${route}`);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="menu-loading">
        <Spinner />
      </div>
    );
  }

  // Render sub-pages
  if (subpage) {
    const handleBack = () => {
      // If on help sub-page, go back to help
      if (subpage === 'help-faqs' || subpage === 'help-feedback') {
        navigate('/menu/help');
      } else {
        navigate('/account');
      }
    };
    
    const subPageTitles: Record<MenuSubPage, string> = {
      account: 'Account Information',
      privacy: 'Privacy',
      notifications: 'Notifications',
      verification: 'Verify Identity',
      help: 'Help',
      'help-faqs': 'FAQs',
      'help-feedback': 'Send us a message',
      about: 'About',
      'developer-tools': 'Developer Tools',
    };

    // Sub-pages that contain MFEs need full height
    const hasMfe = subpage === 'privacy' || subpage === 'notifications' || subpage === 'verification' || subpage === 'help-faqs';
    
    return (
      <div className="menu-container">
        <MenuHeader title={subPageTitles[subpage as MenuSubPage] ?? 'Account'} onBack={handleBack} />
        <div className={`menu-scroll-content ${hasMfe ? 'menu-scroll-content-mfe' : ''}`}>
          {subpage === 'account' && <AccountInformationScreen />}
          {subpage === 'privacy' && <PrivacyScreen />}
          {subpage === 'notifications' && <NotificationsScreen />}
          {subpage === 'verification' && <VerificationScreen />}
          {(subpage === 'help' || subpage === 'help-faqs' || subpage === 'help-feedback') && (
            <HelpScreen 
              currentPage={subpage}
              onNavigate={(page) => navigate(`/account/${page}`)}
            />
          )}
          {subpage === 'about' && <AboutScreen />}
          {subpage === 'developer-tools' && (
            <div className="menu-section">
              <p className="menu-developer-tools-desc">Clear MFE caches and remote containers, then reload. Use if MFEs fail to load or show stale content.</p>
              <Button
                onClick={() => {
                  MfeLoader.clearCacheAndContainers();
                  window.location.reload();
                }}
                variant="secondary"
                size="medium"
                fullWidth
              >
                Reset MFEs
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Use lore_user_name only - NO email fallback for privacy
  const displayName = profile?.lore_user_name || '';
  
  // Avatar URL from sso_object_id. On localhost we use /avatar-proxy to avoid net::ERR_BLOCKED_BY_ORB.
  const avatarUrl = getAvatarUrl(profile?.sso_object_id);
  
  // Get user initials from username only (matching Sidebar behavior)
  // Extracts first letter of each word in the username (handles underscores, hyphens, spaces)
  // Returns maximum of 2 letters
  const userInitials = (() => {
    const username = profile?.lore_user_name;
    if (username) {
      // Split on common separators (underscore, hyphen, space) and get first letter of each part
      const parts = username.split(/[_\-\s]+/).filter(part => part.length > 0);
      if (parts.length >= 2) {
        // If multiple parts, use first letter of first two parts (max 2)
        return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2);
      } else if (parts.length === 1) {
        // If single part, use first letter only
        return parts[0].charAt(0).toUpperCase();
      }
    }
    return '';
  })();
  
  const showAvatarImage = avatarUrl && !avatarError;

  const handleAvatarError = () => {
    console.warn('[MenuScreen] Avatar image failed to load, falling back to initials');
    setAvatarError(true);
  };

  // Verification logic (matching iOS/Android exactly):
  // 1. Null response = show "Verify Identity", no checkmark
  // 2. False response = show "Verify Identity", no checkmark (regardless of enable status)
  // 3. True + enabled = hide button, show checkmark
  // 4. True + disabled = show "Re-Verify", show checkmark
  
  // Extract boolean values (handle both boolean and string types from API)
  const getBooleanValue = (value: any): boolean => {
    if (value === true || value === 'true' || value === 1 || value === '1') return true;
    if (value === false || value === 'false' || value === 0 || value === '0') return false;
    return false; // Default to false for undefined/null
  };
  
  // Only parse boolean values if verificationData exists
  const isVerified = verificationData !== null ? getBooleanValue(verificationData.is_verified) : false;
  const isEnabled = verificationData !== null ? getBooleanValue(verificationData.is_enabled) : false;
  
  // Match iOS logic exactly (from PrimaryActionsSectionView.showButton):
  // if let data = verificationData {
  //   if data.isVerified == false { return true }
  //   else if data.isVerified == true && isEnabled == false { return true }
  //   else { return false }
  // } else {
  //   return true
  // }
  const showVerificationButton = verificationData === null
    ? true  // Null response = show button
    : (isVerified === false
        ? true  // False response = show button
        : (isVerified === true && isEnabled === false
            ? true  // True + disabled = show "Re-Verify"
            : false)); // True + enabled = hide button
  
  // Button label logic (matching iOS PrimaryActionsSectionView.buttonLabel):
  // if let data = verificationData {
  //   if data.isVerified == false { return "Verify Identity" }
  //   else if data.isVerified == true && isEnabled == false { return "Re-Verify" }
  //   else { return "" }
  // } else {
  //   return "Verify Identity"
  // }
  const verificationButtonLabel = verificationData === null
    ? 'Verify Identity'  // Null response
    : (isVerified === false
        ? 'Verify Identity'  // False response
        : (isVerified === true && isEnabled === false
            ? 'Re-Verify'  // True + disabled
            : 'Verify Identity')); // Fallback (shouldn't reach here when button is hidden)
  
  // Show checkmark when verified (regardless of enabled status) - matches iOS/Android
  // iOS shows checkmark when isVerified == true (from ProfileHeaderView)
  const showCheckmark = isVerified === true;

  // Main menu screen
  return (
    <div className="menu-container">
      <MenuHeader title="Account" />
      <div className="menu-scroll-content">
        {/* User Profile Section */}
        <div className="menu-profile-section">
          {showAvatarImage ? (
            <img
              src={avatarUrl || ''}
              alt={displayName}
              className="menu-avatar-image"
              onError={handleAvatarError}
            />
          ) : (
            <div className="menu-avatar-placeholder">
              {userInitials}
            </div>
          )}
          <div className="menu-profile-info">
            <div className="menu-profile-name-row">
              <h2>{displayName}</h2>
              {showCheckmark && (
                <VerifiedUserIcon className="menu-verified-checkmark" />
              )}
            </div>
          </div>
        </div>

        {/* Account Section - matches iOS/Android order */}
        <div className="menu-section">
          <div className="menu-group">
            <MenuItem title="Account Information" onPress={() => handleMenuItem('account')} />
            <MenuItem title="Privacy" onPress={() => handleMenuItem('privacy')} />
            <MenuItem title="Notifications" onPress={() => handleMenuItem('notifications')} />
            {showVerificationButton && (
              <MenuItem title={verificationButtonLabel} onPress={() => handleMenuItem('verification')} />
            )}
          </div>
        </div>

        {/* Help Section - Help and About grouped together */}
        <div className="menu-section">
          <div className="menu-group">
            <MenuItem title="Help" onPress={() => handleMenuItem('help')} />
            <MenuItem title="About" onPress={() => handleMenuItem('about')} />
          </div>
        </div>

        {/* Developer Tools - non-prod only */}
        {(process.env.REACT_APP_ENV === 'nonprod' || process.env.REACT_APP_ENV === 'development' || process.env.NODE_ENV === 'development') && (
          <div className="menu-section">
            <div className="menu-group">
              <MenuItem title="Developer Tools" onPress={() => handleMenuItem('developer-tools')} />
            </div>
          </div>
        )}


        {/* Sign Out Button */}
        <div className="menu-logout-section">
          <Button onClick={handleLogoutClick} variant="secondary" size="medium" fullWidth>
            Sign Out
          </Button>
        </div>

        <ConfirmModal
          open={showSignOutModal}
          title="Sign out"
          message="Are you sure you want to sign out?"
          confirmLabel="Sign out"
          cancelLabel="Cancel"
          onConfirm={handleLogoutConfirm}
          onCancel={handleLogoutCancel}
        />

        {/* Version Info */}
        <div className="menu-version">
          <div>Version 1.0.0</div>
          <div>Lore Web Client</div>
        </div>
      </div>
    </div>
  );
};
